// Share the final championship result as an image, using the device's native
// share sheet (Web Share API with files) and falling back to a PNG download on
// platforms that can't share files (most desktop browsers). The image is drawn
// on a Canvas — no dependencies, works fully offline, matches the app theme.
import type { BadgeId, SeasonState } from "../types";
import { historicAffinity } from "../engine/synergy";
import { playerTeam, playerWccPos } from "../engine/selectors";
import { playerScore } from "../engine/replay";
import { BADGES } from "../data/badges";
import { badgeArt, lockedBadgeArt } from "./badgeArt";
import { chassisModel } from "./helpers";
import { t } from "./i18n";

const W = 1080;
const H = 1482;

// Palette mirrors the CSS custom properties in styles.css.
const C = {
  bg: "#0a0a0d",
  panel: "#141419",
  panel2: "#1c1c24",
  line: "#2a2a35",
  txt: "#f2f2f5",
  muted: "#9a9aa8",
  dim: "#6a6a78",
  red: "#e10600",
  gold: "#d4af37",
  green: "#3bd16f",
  cyan: "#38d6e0",
  mythic: "#c64dff",
  chaos: "#ff7a18",
} as const;

const SANS = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

interface TextOpts {
  size: number;
  weight?: number | string;
  color?: string;
  font?: string;
  align?: CanvasTextAlign;
  baseline?: CanvasTextBaseline;
}

function text(ctx: CanvasRenderingContext2D, str: string, x: number, y: number, o: TextOpts): void {
  ctx.font = `${o.weight ?? 400} ${o.size}px ${o.font ?? SANS}`;
  ctx.fillStyle = o.color ?? C.txt;
  ctx.textAlign = o.align ?? "left";
  ctx.textBaseline = o.baseline ?? "alphabetic";
  ctx.fillText(str, x, y);
}

/** Rounded-rect path helper (avoids relying on ctx.roundRect for older Safari). */
function rrect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function panel(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, fill: string = C.panel): void {
  rrect(ctx, x, y, w, h, 16);
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = C.line;
  ctx.stroke();
}

/**
 * Draw a lineup row: a label, a name, the two-stat readout, and (optionally) the
 * entry's final championship position as a gold badge on the right.
 */
function lineupRow(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  label: string,
  name: string,
  stats: string,
  pos?: string,
  posColor: string = C.gold,
): void {
  const h = 116;
  panel(ctx, x, y, w, h, C.panel2);
  text(ctx, label.toUpperCase(), x + 28, y + 38, { size: 20, weight: 700, color: C.dim });
  text(ctx, name, x + 28, y + 84, { size: 38, weight: 700, color: C.txt });
  if (pos) {
    text(ctx, pos, x + w - 28, y + 48, { size: 28, weight: 800, color: posColor, align: "right" });
    text(ctx, stats, x + w - 28, y + 90, { size: 26, weight: 600, color: C.muted, font: MONO, align: "right" });
  } else {
    text(ctx, stats, x + w - 28, y + 70, { size: 30, weight: 600, color: C.gold, font: MONO, align: "right" });
  }
}

/** Render the championship result card and return the canvas. */
export function renderShareImage(s: SeasonState): HTMLCanvasElement {
  const T = t();
  const st = s.standings!;
  // A result image without the player is nonsensical (the caller has final
  // standings), so fall back to the first team only to satisfy the non-null type.
  const player = playerTeam(s) ?? s.opponents[0]!;
  const syn = player.synergy;
  const wcc = st.constructors[0]!;
  const wdc = st.drivers[0]!;
  const wccPos = playerWccPos(st);
  const won = wcc.isPlayer || wdc.isPlayer;
  // Final WDC position of one of the player's drivers, matched by name+team.
  const driverPos = (name: string, team: string): number =>
    st.drivers.findIndex((d) => d.driverName === name && d.driverTeam === team) + 1;

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;

  // Background + top accent bar.
  ctx.fillStyle = C.bg;
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = C.red;
  ctx.fillRect(0, 0, W, 12);

  const P = 64;
  const innerW = W - P * 2;

  // Header: "GRAND PRIX DRAFT LEGENDS" with "GRAND PRIX" in red.
  ctx.textBaseline = "alphabetic";
  const hy = 104;
  text(ctx, "GRAND PRIX", P, hy, { size: 44, weight: 800, color: C.red });
  ctx.font = "800 44px " + SANS;
  const markw = ctx.measureText("GRAND PRIX").width;
  text(ctx, " DRAFT LEGENDS", P + markw, hy, { size: 44, weight: 800, color: C.txt });
  text(ctx, s.daily ? T.imgDaily(s.day) : T.imgSeasonComplete, W - P, hy - 6, {
    size: 22,
    weight: 700,
    color: C.gold,
    font: MONO,
    align: "right",
  });

  // Champions panel.
  let y = 150;
  const champH = 360;
  panel(ctx, P, y, innerW, champH);
  const cx = W / 2;
  text(ctx, T.imgWccChampion, cx, y + 64, { size: 24, weight: 700, color: C.gold, align: "center" });
  text(ctx, wcc.name + (wcc.isPlayer ? "  ◈" : ""), cx, y + 124, {
    size: 56,
    weight: 800,
    color: wcc.isPlayer ? C.gold : C.txt,
    align: "center",
  });
  text(ctx, T.imgWdcChampion, cx, y + 212, { size: 24, weight: 700, color: C.gold, align: "center" });
  text(ctx, `${wdc.driverName}${wdc.isPlayer ? "  ◈" : ""}`, cx, y + 272, {
    size: 52,
    weight: 800,
    color: wdc.isPlayer ? C.gold : C.txt,
    align: "center",
  });
  text(
    ctx,
    won ? T.imgTitleSecured : T.imgNoTitle,
    cx,
    y + 330,
    { size: 26, weight: 700, color: won ? C.green : C.muted, align: "center" },
  );

  // Highlight a championship won under the harder modes: From Memory (card stats
  // hidden during the draft) and Chaos (wild per-race luck swings). Both can show.
  y += champH + 40;
  if (won && s.fromMemory) {
    text(ctx, T.imgMemoryWin, cx, y, { size: 30, weight: 800, color: C.mythic, align: "center" });
    y += 48;
  }
  if (won && s.chaos) {
    text(ctx, T.imgChaosWin, cx, y, { size: 30, weight: 800, color: C.chaos, align: "center" });
    y += 48;
  }

  // Player team + final positions.
  y += 4;
  text(ctx, player.name.toUpperCase(), P, y, { size: 24, weight: 700, color: C.dim });
  text(
    ctx,
    T.imgPositions(wccPos, player.points),
    W - P,
    y,
    { size: 26, weight: 600, color: C.muted, font: MONO, align: "right" },
  );

  // Lineup (drivers + chassis) with synergy-adjusted stats.
  y += 28;
  const d1 = player.d1;
  const d2 = player.d2;
  const ch = player.chassis;
  const chModel = chassisModel(ch);
  // Per-driver: only the driver who raced this chassis gets the historic pace edge.
  const aff1 = historicAffinity(d1, ch);
  const aff2 = historicAffinity(d2, ch);
  const A = T.attrs;
  lineupRow(
    ctx, P, y, innerW, T.driver1, `${d1.name} ${d1.team}`,
    `${A.pace.abbr} ${d1.pace + aff1.pace}  ${A.consistency.abbr} ${d1.consistency + syn.consistency + aff1.consistency}`,
    `${T.thWdc} P${driverPos(d1.name, d1.team)}`,
  );
  y += 132;
  lineupRow(
    ctx, P, y, innerW, T.driver2, `${d2.name} ${d2.team}`,
    `${A.pace.abbr} ${d2.pace + aff2.pace}  ${A.consistency.abbr} ${d2.consistency + syn.consistency + aff2.consistency}`,
    `${T.thWdc} P${driverPos(d2.name, d2.team)}`,
  );
  y += 132;
  lineupRow(
    ctx, P, y, innerW, T.chassis, `${ch.team} ${chModel}`,
    `${A.motor.abbr} ${ch.motor + syn.motor}  ${A.aero.abbr} ${ch.aero}`,
    `${T.thWcc} P${wccPos}`,
  );

  // Director row (the mandatory 4th pick): trait short-name + reliability /
  // development. Null only for seasons drafted before the director slot existed.
  const dir = player.director;
  if (dir) {
    y += 132;
    const DA = T.directorAttrs;
    const traitShort = T.directorTrait(dir.signature).split(" — ")[0]!;
    lineupRow(
      ctx, P, y, innerW, T.director, T.directorName(dir.name),
      `${DA.reliability.abbr} ${dir.reliability}  ${DA.development.abbr} ${dir.devSlope}`,
      traitShort, C.cyan,
    );
  }

  // Synergy reasons (if any) just below the lineup.
  y += 132;
  if (syn.reasons.length) {
    text(ctx, T.imgSynergies, P, y + 8, { size: 20, weight: 700, color: C.dim });
    const reasons = syn.reasons.slice(0, 3).map((r) => T.synReason(r)).join("   ◇   ");
    text(ctx, "◇ " + reasons, P, y + 44, { size: 24, weight: 600, color: C.green });
  }

  // Footer.
  text(ctx, T.imgFooter, cx, H - 56, {
    size: 26,
    weight: 600,
    color: C.muted,
    align: "center",
  });

  return canvas;
}

export type ShareResult = "shared" | "downloaded" | "cancelled" | "error";

const FILENAME = "grand-prix-draft-legends.png";

// A rendered result image plus everything the UI needs to preview, share, copy
// or download it without re-rendering the canvas each time.
export interface ResultImage {
  readonly blob: Blob;
  readonly dataUrl: string;
  readonly caption: string;
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}

function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after the click has been processed.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Load an image URL into an HTMLImageElement (resolves null on failure). */
function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/** Greedy word-wrap into at most `maxLines` lines that fit `maxWidth`. */
function wrapLines(ctx: CanvasRenderingContext2D, str: string, maxWidth: number, maxLines: number): string[] {
  const lines: string[] = [];
  let cur = "";
  for (const word of str.split(" ")) {
    const test = cur ? `${cur} ${word}` : word;
    if (cur && ctx.measureText(test).width > maxWidth) {
      lines.push(cur);
      cur = word;
    } else {
      cur = test;
    }
  }
  if (cur) lines.push(cur);
  return lines.slice(0, maxLines);
}

/**
 * Render the player's emblem collection as a shareable grid: every badge as a
 * gold medallion (its own art when earned, the locked medallion otherwise) with
 * its name beneath. Async because it preloads the badge art onto the canvas.
 */
export async function renderBadgesImage(unlocked: readonly BadgeId[]): Promise<HTMLCanvasElement> {
  const T = t();
  const COLS = 3;
  const P = 64;
  const GAP = 28;
  const ART = 156;
  const HEADER = 176;
  const FOOTER = 120;
  const CELL_H = ART + 96;
  const innerW = W - P * 2;
  const cellW = (innerW - GAP * (COLS - 1)) / COLS;
  const rows = Math.ceil(BADGES.length / COLS);
  const isUnlocked = (id: BadgeId): boolean => unlocked.includes(id);
  const h = HEADER + rows * CELL_H + FOOTER;

  // Preload every medallion we'll draw before touching the canvas.
  const lockImg = await loadImage(lockedBadgeArt);
  const artImgs = new Map<BadgeId, HTMLImageElement>();
  await Promise.all(
    BADGES.map(async (b) => {
      const src = isUnlocked(b.id) ? badgeArt(b.id) : undefined;
      if (!src) return;
      const img = await loadImage(src);
      if (img) artImgs.set(b.id, img);
    }),
  );

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = C.bg;
  ctx.fillRect(0, 0, W, h);
  ctx.fillStyle = C.red;
  ctx.fillRect(0, 0, W, 12);

  // Header: wordmark + "MY EMBLEMS" tag + unlocked count.
  const hy = 104;
  text(ctx, "GRAND PRIX", P, hy, { size: 44, weight: 800, color: C.red });
  ctx.font = "800 44px " + SANS;
  const markw = ctx.measureText("GRAND PRIX").width;
  text(ctx, " DRAFT LEGENDS", P + markw, hy, { size: 44, weight: 800, color: C.txt });
  text(ctx, T.imgEmblems, W - P, hy - 6, { size: 22, weight: 700, color: C.gold, font: MONO, align: "right" });
  const count = BADGES.filter((b) => isUnlocked(b.id)).length;
  text(ctx, T.imgEmblemsCount(count, BADGES.length), P, hy + 44, { size: 26, weight: 600, color: C.muted, font: MONO });

  // Medallion grid.
  BADGES.forEach((b, i) => {
    const col = i % COLS;
    const row = Math.floor(i / COLS);
    const cx = P + col * (cellW + GAP) + cellW / 2;
    const ay = HEADER + row * CELL_H;
    const art = artImgs.get(b.id);
    if (art) {
      ctx.drawImage(art, cx - ART / 2, ay, ART, ART);
    } else if (isUnlocked(b.id)) {
      // Earned but no art ships yet: a gold medallion with a medal glyph.
      ctx.beginPath();
      ctx.arc(cx, ay + ART / 2, ART / 2, 0, Math.PI * 2);
      ctx.fillStyle = C.panel2;
      ctx.fill();
      ctx.lineWidth = 6;
      ctx.strokeStyle = C.gold;
      ctx.stroke();
      text(ctx, "🏅", cx, ay + ART / 2, { size: 76, align: "center", baseline: "middle" });
    } else if (lockImg) {
      ctx.globalAlpha = 0.5;
      ctx.drawImage(lockImg, cx - ART / 2, ay, ART, ART);
      ctx.globalAlpha = 1;
    }
    ctx.font = `700 26px ${SANS}`;
    const lines = wrapLines(ctx, T.badgeName(b.id), cellW - 8, 2);
    lines.forEach((ln, li) => {
      text(ctx, ln, cx, ay + ART + 40 + li * 32, {
        size: 26,
        weight: 700,
        color: isUnlocked(b.id) ? C.gold : C.dim,
        align: "center",
      });
    });
  });

  text(ctx, T.imgFooter, W / 2, h - 56, { size: 26, weight: 600, color: C.muted, align: "center" });
  return canvas;
}

/** Render the emblems image once and bundle it with its share caption. */
export async function buildBadgesImage(unlocked: readonly BadgeId[]): Promise<ResultImage | null> {
  const canvas = await renderBadgesImage(unlocked);
  const blob = await canvasToBlob(canvas);
  if (!blob) return null;
  const count = BADGES.filter((b) => unlocked.includes(b.id)).length;
  const caption = t().shareBadgesCaption(count, BADGES.length);
  return { blob, dataUrl: canvas.toDataURL("image/png"), caption };
}

// Canonical site, and a deep link that drops a friend straight into today's
// Daily Challenge (see App.start). Always the production host — a shared
// localhost link would be useless.
export const SITE_URL = "https://app.gpdraft.com/";
export const DAILY_DEEPLINK = SITE_URL + "?daily";

/** Render the result image once and bundle it with its share caption. */
export async function buildResultImage(s: SeasonState): Promise<ResultImage | null> {
  const canvas = renderShareImage(s);
  const blob = await canvasToBlob(canvas);
  if (!blob) return null;
  const st = s.standings!;
  const won = st.constructors[0]!.isPlayer || st.drivers[0]!.isPlayer;
  const teamName = playerTeam(s)?.name ?? "My team";
  const T = t();
  const caption = won ? T.shareCaptionWon(teamName) : T.shareCaptionLost(teamName);
  return { blob, dataUrl: canvas.toDataURL("image/png"), caption };
}

/**
 * The Daily Challenge share message — text only, on purpose. Everyone gets the
 * same draft pool, so sharing the result *image* would leak your exact lineup
 * and let a friend copy it. Instead we share a scoreboard (final position,
 * constructor points, each driver's points) plus a challenge and the link, so
 * they have to beat you rather than clone you.
 */
export function buildDailyShareText(s: SeasonState): string {
  const st = s.standings;
  if (!st) return DAILY_DEEPLINK;
  const T = t();
  const teamName = playerTeam(s)?.name ?? "My team";
  const won = st.constructors[0]!.isPlayer || st.drivers[0]!.isPlayer;
  // Ranking fields via the same derivation the server verifies (playerScore), so
  // the shared scoreboard can't disagree with the leaderboard. The per-driver
  // breakdown stays a "+"-joined string (e.g. "18 + 12"), which the scalar sum
  // in playerScore.driverPts doesn't carry.
  const score = playerScore(st);
  const driverPts = st.drivers
    .filter((d) => d.isPlayer)
    .map((d) => d.points)
    .join(" + ");
  return `${T.shareCaptionDaily(teamName, won, score?.wccPos ?? 0, score?.teamPts ?? 0, driverPts)} ${DAILY_DEEPLINK}`;
}

/** Whether the platform can hand a PNG file to the native share sheet. */
export function canDeviceShare(img: ResultImage): boolean {
  const file = new File([img.blob], FILENAME, { type: "image/png" });
  return (
    typeof navigator.canShare === "function" &&
    navigator.canShare({ files: [file] }) &&
    typeof navigator.share === "function"
  );
}

/** Whether the platform can write an image to the clipboard. */
export function canCopyImage(): boolean {
  return typeof ClipboardItem !== "undefined" && typeof navigator.clipboard?.write === "function";
}

/**
 * Hand the image to the OS share sheet. Falls back to a download when the
 * platform can't share files (e.g. desktop Chrome/Firefox).
 */
export async function shareViaDevice(img: ResultImage): Promise<ShareResult> {
  const file = new File([img.blob], FILENAME, { type: "image/png" });
  const shareData: ShareData = { files: [file], title: "Grand Prix Draft Legends", text: img.caption };
  if (canDeviceShare(img)) {
    try {
      await navigator.share(shareData);
      return "shared";
    } catch (err) {
      // User dismissed the sheet — don't fall through to a surprise download.
      if (err instanceof DOMException && err.name === "AbortError") return "cancelled";
      // Any other failure: fall back to a download so the user still gets the image.
    }
  }
  download(img.blob, FILENAME);
  return "downloaded";
}

/** Copy the image to the clipboard. Returns false if unsupported or denied. */
export async function copyImageToClipboard(img: ResultImage): Promise<boolean> {
  if (!canCopyImage()) return false;
  try {
    await navigator.clipboard.write([new ClipboardItem({ "image/png": img.blob })]);
    return true;
  } catch {
    return false;
  }
}

/** Save the image as a PNG download. */
export function downloadImage(img: ResultImage): void {
  download(img.blob, FILENAME);
}
